// Loads the action catalog and keeps it fresh: once on mount, and again when
// the user returns to the page after a spell away.

import { useCallback, useEffect, useRef, useState } from 'react';
import { t } from '@lingui/macro';

import ActionsApi from '../../api/actions';
import CategoriesApi from '../../api/categories';
import { assembleChains } from './atUtils';

const ACTIONS_PAGE_SIZE = 200;

// Returning to the page refetches the whole catalog. Within this window the
// data is treated as still fresh, so tabbing back and forth costs nothing.
const CATALOG_CACHE_MS = 5 * 60 * 1000;

export default function useActionsCatalog({ pushToast }) {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const lastFetchRef = useRef(0);
  const fetchAllRef = useRef(null);

  const fetchAll = useCallback(async () => {
    // `loading` is only true on initial mount; refetches stay silent so they
    // don't unmount an open drawer/modal and wipe in-progress edits.
    const [actionsResponse, categoriesResponse] = await Promise.all([
      ActionsApi.fetchActionsList({ limit: ACTIONS_PAGE_SIZE }),
      CategoriesApi.listCategories(),
    ]);

    if (actionsResponse.errors) {
      pushToast({ kind: 'error', title: t`Failed to load actions` });
      setLoading(false);
      return null;
    }

    // Cross-walk category_uuid → category name so the UI can render the
    // KDS-tinted tag with the right palette entry.
    const categoriesById = {};
    (categoriesResponse.data || []).forEach((c) => {
      categoriesById[c.id] = c.attributes.name;
    });
    const enriched = (actionsResponse.data || []).map((row) => ({
      ...row,
      attributes: {
        ...row.attributes,
        category_name: categoriesById[row.attributes.category_uuid],
      },
    }));

    const chains = assembleChains(enriched);
    setActions(chains);
    setRefreshKey((n) => n + 1);
    lastFetchRef.current = Date.now();
    setLoading(false);
    // Return the fresh chains so callers awaiting fetchAll read them directly
    // (the `actions` closure stays pre-fetch / stale).
    return chains;
  }, [pushToast]);

  fetchAllRef.current = fetchAll;

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch when the user returns to the page (e.g. after editing an
  // integration's action binding on IntegrationEditPage). Covers both
  // tab-switch (focus) and bfcache restores (pageshow.persisted). Only this
  // path is rate limited — refetches that follow a mutation call fetchAll
  // directly and must always hit the network, or a save would not show its own
  // result.
  useEffect(() => {
    const refetchOnReturn = () => {
      if (Date.now() - lastFetchRef.current < CATALOG_CACHE_MS) return;
      fetchAllRef.current();
    };
    const onFocus = () => refetchOnReturn();
    const onPageShow = (e) => {
      if (e.persisted) refetchOnReturn();
    };
    window.addEventListener('focus', onFocus);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, []);

  return { actions, setActions, loading, fetchAll, refreshKey };
}
